/**
 * Blueprint clone detection.
 *
 * Blueprint clones are the only line items that carry blueprint provenance:
 * `blueprintKey` (set at apply time going forward) or the generated name
 * pattern `<blueprint item name>_<source name>` (legacy clones).
 *
 * This matters because blueprint apply also unmarks the ORIGINAL line items
 * (markedForDelivery: false), turning them into the customer-facing reference
 * set. Detecting clones tells you a campaign has a reference set — which is
 * the only situation where markedForDelivery === false means "deliberately
 * excluded" rather than a stale/auto-unmarked flag.
 */

import { ALL_BLUEPRINTS } from './blueprints';

const BLUEPRINT_NAME_PREFIXES = ALL_BLUEPRINTS.flatMap((blueprint) =>
  Object.values(blueprint.strategySets || {}).flatMap((set) =>
    (set.lineItems || []).map((item) => `${item.name}_`)
  )
);

export const isBlueprintClone = (lineItem) =>
  !!lineItem?.blueprintKey ||
  BLUEPRINT_NAME_PREFIXES.some((prefix) => (lineItem?.name || '').startsWith(prefix));

/**
 * True when a line item list contains blueprint clones — i.e. a blueprint was
 * applied and markedForDelivery === false on other items means "reference".
 */
export const hasBlueprintReferenceSet = (lineItems) =>
  Array.isArray(lineItems) && lineItems.some(isBlueprintClone);
